// Home "overview" bento: a tall FrothAI card that leads, then six feature cards,
// each with a small, crisp preview of that part of the product built in CSS.
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bug,
  Check,
  ClipboardCheck,
  Code2,
  FileCode2,
  Gauge,
  LayoutList,
  Play,
  RefreshCw,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { VscAzureDevops } from "react-icons/vsc";
import FrothAiIcon from "../common/FrothAiIcon";
import fMark from "../../../assets/F Logo (1).png";
import jiraLogo from "../../../assets/icons/jira.png";
import planeLogo from "../../../assets/icons/plane.svg";
import "../../css/Bento.css";

function UnifiedPreview() {
  const rows = [
    ["TC-101", 76, "Passed", "pass"],
    ["TC-102", 58, "Failed", "fail"],
    ["TC-103", 66, "Blocked", "block"],
  ];
  return (
    <div className="bx-window">
      {rows.map(([id, w, label, tone]) => (
        <span className="bx-row" key={id}>
          <span className="bx-id">{id}</span>
          <span className="bx-line" style={{ width: `${w}%` }} />
          <span className={`bx-pill bx-pill--${tone}`}>{label}</span>
        </span>
      ))}
    </div>
  );
}

function AutomationPreview() {
  return (
    <div className="bx-code">
      <span className="bx-code__chip">
        <FileCode2 size={12} /> checkout.spec.ts
      </span>
      <code>
        <span>
          <i className="k">test</i>(<i className="s">'guest checkout'</i>, <i className="k">async</i> () =&gt; {"{"}
        </span>
        <span className="in">
          <i className="k">await</i> page.<i className="f">click</i>(<i className="s">'#pay'</i>);
        </span>
        <span className="in">
          <i className="k">await</i> <i className="f">expect</i>(order).<i className="f">toBeVisible</i>();
        </span>
        <span>{"});"}</span>
      </code>
      <span className="bx-code__run">
        <Play size={11} fill="currentColor" /> Run
      </span>
    </div>
  );
}

function LabsPreview() {
  return (
    <div className="bx-labs">
      <span className="bx-lab bx-lab--perf">
        <Gauge size={15} />
        <svg viewBox="0 0 60 24" preserveAspectRatio="none">
          <path d="M0 18 L10 14 L18 16 L26 8 L34 11 L42 5 L50 9 L60 3" />
        </svg>
      </span>
      <span className="bx-lab bx-lab--sec">
        <ShieldCheck size={15} />
        <span className="bx-scan" />
      </span>
      <span className="bx-lab bx-lab--code">
        <Code2 size={15} />
        <span className="bx-bars">
          {[60, 85, 45, 70].map((h, i) => (
            <i key={i} style={{ height: `${h}%` }} />
          ))}
        </span>
      </span>
    </div>
  );
}

function SyncPreview() {
  return (
    <div className="bx-sync">
      <span className="bx-sync__tools">
        <span className="bx-logo">
          <img src={jiraLogo} alt="" />
        </span>
        <span className="bx-logo">
          <img src={planeLogo} alt="" />
        </span>
        <span className="bx-logo">
          <VscAzureDevops color="#0078D4" size={16} />
        </span>
      </span>
      <svg className="bx-sync__links" viewBox="0 0 100 90" preserveAspectRatio="none">
        <path d="M0 15 C50 15 50 45 100 45" />
        <path d="M0 45 L100 45" />
        <path d="M0 75 C50 75 50 45 100 45" />
      </svg>
      <span className="bx-sync__hub">
        <img src={fMark} alt="" />
        <RefreshCw size={11} className="bx-sync__spin" />
      </span>
    </div>
  );
}

function MobilePreview() {
  return (
    <div className="bx-mobile">
      <span className="bx-phone">
        <span className="bx-phone__notch" />
        {[80, 64, 72].map((w, i) => (
          <span className="bx-phone__row" key={i}>
            <span className="bx-phone__dot" />
            <span className="bx-line" style={{ width: `${w}%` }} />
          </span>
        ))}
      </span>
      <span className="bx-toast">
        <Bug size={12} /> Bug reported
      </span>
    </div>
  );
}

function ReportsPreview() {
  return (
    <div className="bx-report">
      <span className="bx-report__doc">
        <span className="bx-line bx-line--head" />
        <span className="bx-report__bars">
          {[40, 65, 52, 88].map((h, i) => (
            <i key={i} style={{ height: `${h}%` }} />
          ))}
        </span>
      </span>
      <span className="bx-stamp">
        <Check size={12} strokeWidth={3} /> Signed off
      </span>
    </div>
  );
}

const cards = [
  {
    title: "Unified test management",
    text: "Requirements, test cases, plans, cycles and defects in one place.",
    icon: LayoutList,
    tone: "blue",
    Preview: UnifiedPreview,
  },
  {
    title: "Automation with less code",
    text: "Record or generate Playwright and Appium scripts, then run them live.",
    icon: Code2,
    tone: "violet",
    Preview: AutomationPreview,
  },
  {
    title: "Performance, security & code labs",
    text: "Load tests, security scans and code analysis without extra tools.",
    icon: Gauge,
    tone: "mint",
    Preview: LabsPreview,
  },
  {
    title: "Two-way sync with Jira, Plane & Azure DevOps",
    text: "Keep requirements and defects in step with the tools you use.",
    icon: RefreshCw,
    tone: "sky",
    Preview: SyncPreview,
  },
  {
    title: "Mobile app for iOS & Android",
    text: "Report bugs and browse tests from your phone.",
    icon: Smartphone,
    tone: "peach",
    Preview: MobilePreview,
  },
  {
    title: "Reports and sign-off",
    text: "Test plan summary reports with review and sign-off.",
    icon: ClipboardCheck,
    tone: "pink",
    Preview: ReportsPreview,
  },
];

export default function Principles() {
  return (
    <div className="home-bento">
      <Link to="/froth-ai" className="home-bento__card bx-ai">
        <span className="bx-ai__aurora" aria-hidden="true" />
        <span className="bx-ai__pill">
          <FrothAiIcon size={14} /> FrothAI
        </span>

        <div className="bx-chat" aria-hidden="true">
          <span className="bx-chat__ask">Generate test cases for guest checkout</span>
          <span className="bx-chat__reply">
            <span className="bx-chat__avatar">
              <FrothAiIcon size={14} />
            </span>
            <span className="bx-chat__out">
              <span className="bx-out" style={{ "--d": 0 }}>
                <Check size={11} strokeWidth={3} /> Guest pays by card
              </span>
              <span className="bx-out" style={{ "--d": 1 }}>
                <Check size={11} strokeWidth={3} /> New delivery address
              </span>
              <span className="bx-out bx-out--script" style={{ "--d": 2 }}>
                <FileCode2 size={11} /> Playwright script
              </span>
            </span>
          </span>
        </div>

        <div className="bx-ai__foot">
          <h3 className="bx-ai__title">AI test generation made easy</h3>
          <p className="bx-ai__text">From requirements to Playwright and Appium scripts.</p>
          <span className="bx-ai__more">
            Explore FrothAI <ArrowRight size={15} />
          </span>
        </div>
      </Link>

      {cards.map(({ title, text, icon: Icon, tone, Preview }) => (
        <article className={`home-bento__card bx-card bx-card--${tone}`} key={title}>
          <div className="bx-card__head">
            <span className="bx-card__icon" aria-hidden="true">
              <Icon size={16} />
            </span>
            <h3 className="bx-card__title">{title}</h3>
          </div>
          <p className="bx-card__text">{text}</p>
          <div className="bx-card__preview" aria-hidden="true">
            <Preview />
          </div>
        </article>
      ))}
    </div>
  );
}
